import { useState, useEffect, useRef, useCallback, FC } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import useRefMounted from 'src/hooks/useRefMounted';
import parse from 'html-react-parser';
import { uniqBy } from 'lodash';
import { StaticDatePicker } from '@mui/lab';
// import { StaticDatePicker } from '@mui/x-date-pickers';
import {
  addDays,
  startOfDay,
  parseISO,
  isEqual,
  format,
  addMinutes,
  isAfter,
  isBefore,
  addMilliseconds,
  subMilliseconds
} from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc, getTimezoneOffset } from 'date-fns-tz';
import useWindowDimensions from 'src/hooks/useWindowDimensions';
// import StickyBox from 'react-sticky-box';
import {
  Alert,
  AlertTitle,
  Grid,
  Card,
  CardContent,
  Divider,
  Typography,
  Box,
  Dialog,
  DialogContent,
  MenuList,
  MenuItem,
  ListItemText,
  Paper,
  Button,
  Avatar,
  ListItemAvatar,
  DialogActions,
  CircularProgress,
  TextField,
  Tooltip,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';

import {
  HookFormAutocompleteField,
  HookFormCountryField,
  HookFormInputField,
  HookFormMultilineInputField,
  HookFormMultiAutocompleteField,
  HookFormCheckboxField
} from 'src/components/FormFields';

import PublicPromoCodeInput from './PublicPromoCodeInput';

import ChevronRightTwoToneIcon from '@mui/icons-material/ChevronRightTwoTone';
import CalendarTodayTwoToneIcon from '@mui/icons-material/CalendarTodayTwoTone';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import RestartAltTwoToneIcon from '@mui/icons-material/RestartAltTwoTone';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import tenantMappings from 'src/utils/tenantMappings';
import { Mixpanel } from 'src/contexts/MixPanelContext';

import BookingRequestForm from './BookingRequestForm';

const sortOnlineBookingCategoryAsc = (a, b) => {
  if (a.label.trim() < b.label.trim()) {
    return -1;
  }
  if (a.label.trim() > b.label.trim()) {
    return 1;
  }
  return 0;
};

const sortServiceNameAsc = (a, b) => {
  if (a.onlineBookingName.trim() < b.onlineBookingName.trim()) {
    return -1;
  }
  if (a.onlineBookingName.trim() > b.onlineBookingName.trim()) {
    return 1;
  }
  return 0;
};

const sortPractAsc = (a, b) => {
  if (a.providerName < b.providerName) {
    return -1;
  }
  if (a.providerName > b.providerName) {
    return 1;
  }
  return 0;
};

const sortPractLabelAsc = (a, b) => {
  if (a.label < b.label) {
    return -1;
  }
  if (a.label > b.label) {
    return 1;
  }
  return 0;
};

const dictionaryMappings = tenantMappings?.dictionaryMappings;
const onlineBookingMappings = tenantMappings?.onlineBookingMappings;

const Scheduler: FC<any> = ({
  contentHTML,
  setCurrentScreen,
  tenantId,
  servicesList,
  onlineBookingPageId,
  urlQueryParams,
  customFields,
  allowedDaysInFuture,
  locationDetails
}) => {
  const { height, width } = useWindowDimensions();
  const theme = useTheme();
  const navigate = useNavigate();
  const isMountedRef = useRefMounted();

  const mobile = useMediaQuery(theme.breakpoints.down('sm'));

  const tenantIdLowerCase = tenantId?.toLowerCase();

  const [openRequestForm, setOpenRequestForm] = useState(false);
  const [category, setCategory] = useState('');
  const [togglePracLoc, setTogglePracLoc] = useState('');
  const [selectedServiceName, setSelectedServiceName] = useState('');
  const [selectedServiceDescription, setSelectedServiceDescription] =
    useState('');
  const [selectedPractitioner, setSelectedPractitioner] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');

  const [selectedServiceId, setSelectedServiceId] = useState('');

  const [availableServices, setAvailableServices] = useState([]);

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [practitionerOptions, setPractitionerOptions] = useState([]);

  const [isFetchingTimeSlots, setIsFetchingTimeSlots] = useState(false);
  const [freeSlots, setFreeSlots] = useState([]);
  const [dateValue, setDateValue] = useState(new Date());
  const [selectedAppointmentTime, setSelectedAppointmentTime] = useState(null);

  const [paymentURL, setPaymentURL] = useState('');
  const [openPaymentPrompt, setOpenPaymentPrompt] = useState(false);

  const [errorMessage, setErrorMessage] = useState('');
  const [warningMessage, setWarningMessage] = useState('');

  const [validPromoCode, setValidPromoCode] = useState([]);
  const [isCheckingPromoCode, setIsCheckingPromoCode] = useState(false);

  const [selectedLocationTimeZone, setSelectedLocationTimeZone] =
    useState('Asia/Singapore');

  const [dateSelectionCount, setDateSelectionCount] = useState(0);

  const getTimeZoneDifference =
    getTimezoneOffset(selectedLocationTimeZone, new Date()) || 28800000;
  const selectedLocationTimeZoneOffsetConv =
    getTimeZoneDifference / 1000 / 60 / 60;

  // const cutOffMinutes = 60 * 20
  const cutOffDateTime = addMinutes(
    // new Date(), // first version
    utcToZonedTime(new Date(), selectedLocationTimeZone), // second version
    onlineBookingMappings?.getOnlineBookingWindowPeriod(tenantIdLowerCase)[
      'leadTimeinMinutes'
    ][
      availableServices.find(
        (service) =>
          service.onlineBookingCategory === category &&
          service.onlineBookingName === selectedServiceName &&
          service.providerName === selectedPractitioner &&
          service.locationName === selectedLocation
      )?.practitionerId
    ] ||
      onlineBookingMappings?.getOnlineBookingWindowPeriod(tenantIdLowerCase)[
        'leadTimeinMinutes'
      ]['general']
  );
  // console.log('cutoff-time', cutOffDateTime);

  const warningTime = addMinutes(
    new Date(),
    onlineBookingMappings?.getWarningMessage(tenantIdLowerCase)[
      'warningPeriodinMinutes'
    ]
  );

  useEffect(() => {
    const showWarningMessage = selectedAppointmentTime
      ? isBefore(selectedAppointmentTime?.startTime, warningTime)
      : false;

    if (showWarningMessage) {
      setWarningMessage(
        onlineBookingMappings?.getWarningMessage(tenantIdLowerCase)[
          'warningMessage'
        ]
      );
    } else {
      setWarningMessage('');
    }

    // console.log('selectedAppointmentTime', selectedAppointmentTime);
    // console.log('warningTime', warningTime);
    // console.log('showWarningMessage', showWarningMessage);
    // console.log('warningMessage', warningMessage);
  }, [selectedAppointmentTime]);

  const mappedWordsToUse =
    dictionaryMappings?.getMappedWordToUse(tenantIdLowerCase);
  const getMappedWordToUse = (word) => {
    const fallback = word;

    const result = mappedWordsToUse[word];

    if (result) {
      return result;
    } else {
      return fallback;
    }
  };

  const onlineBookingPageFieldsToShow =
    onlineBookingMappings?.getOptionalFieldsToShow(tenantIdLowerCase);
  const getOptionalFieldsToShow = (fieldName) => {
    const fallback = false;

    const result = onlineBookingPageFieldsToShow[fieldName];

    if (result) {
      return result;
    } else {
      return fallback;
    }
  };

  const onlineBookingPageFieldMandatoryRequirements =
    onlineBookingMappings?.getFieldMandatoryRequirements(tenantIdLowerCase);
  const getFieldMandatoryRequirements = (fieldName) => {
    const fallback = false;

    const result = onlineBookingPageFieldMandatoryRequirements[fieldName];

    if (result) {
      return result;
    } else {
      return fallback;
    }
  };

  const onlineBookingPageFieldLabels =
    onlineBookingMappings?.getFieldLabels(tenantIdLowerCase);
  const getFieldLabels = (fieldName) => {
    const fallback = fieldName;

    const result = onlineBookingPageFieldLabels[fieldName];

    if (result) {
      return result;
    } else {
      return fallback;
    }
  };

  const datePickerRef = useRef(null);
  const executeScrollToDatePicker = () => {
    if (datePickerRef.current) {
      return datePickerRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const originPickRef = useRef(null);
  const executeScrollToOriginPick = () => {
    if (originPickRef.current) {
      return originPickRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const firstPickRef = useRef(null);
  const executeScrollToFirstPick = () => {
    if (firstPickRef.current) {
      return firstPickRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const secondPickRef = useRef(null);
  const executeScrollToSecondPick = () => {
    if (secondPickRef.current) {
      return secondPickRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const thirdPickRef = useRef(null);
  const executeScrollToThirdPick = () => {
    if (thirdPickRef.current) {
      return thirdPickRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const summaryRef = useRef(null);
  const executeScrollToSummary = () => {
    if (summaryRef.current) {
      return summaryRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const capitaliseFirstLetterOfWord = (phrase) => {
    //split the above string into an array of strings
    //whenever a blank space is encountered
    const arr = phrase.toLowerCase().split(' ');

    //loop through each element of the array and capitalize the first letter.
    for (var i = 0; i < arr.length; i++) {
      arr[i] = arr[i].charAt(0).toUpperCase() + arr[i].slice(1);
    }

    //Join all the elements of the array back into a string
    //using a blankspace as a separator
    const transformedPhrase = arr.join(' ');

    return transformedPhrase;
  };

  const enquiry =
    customFields && JSON.parse(customFields)?.enquiry
      ? JSON.parse(customFields)?.enquiry
      : [];

  const customFieldDefaults =
    enquiry?.length > 0
      ? enquiry.reduce(
          (accum, val, index) => ({
            ...accum,
            [`question${index}`]: val['type'] === 'multiSelect' ? [] : ''
          }),
          {}
        )
      : {};

  // console.log(customFieldDefaults);

  // Registering React Hook Form
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: {
      preferredName: '',
      lastName: '',
      mobileCountryCode: '65',
      mobile: '',
      email: urlQueryParams?.email || '',
      ...customFieldDefaults
    }
  });

  const onSubmit = async (data) => {
    // console.log(data);
    // console.log(enquiry)

    if (!isAfter(selectedAppointmentTime?.startTime, cutOffDateTime)) {
      // console.log(cutOffDateTime)
      throw new Error('Time slot is no longer available');
    }

    const enquiryResponses = enquiry
      ?.filter((seg) => !seg?.type?.toLowerCase().includes('display'))
      ?.map((seg, i) => {
        const questionNum = `question${i}`;
        return {
          question: seg.question,
          type: seg.type,
          answer: data[questionNum] || ''
        };
      });

    const matchedService = servicesList.find(
      (service) =>
        service.onlineBookingCategory === category &&
        service.onlineBookingName === selectedServiceName &&
        service.providerName === selectedPractitioner &&
        service.locationName === selectedLocation
    );

    const finalData = {
      // endTime: selectedAppointmentTime.endTime,
      endTime: zonedTimeToUtc(
        selectedAppointmentTime.endTime,
        selectedLocationTimeZone
      ),
      onlineBookingPageId: onlineBookingPageId,
      patientEmail: data.email?.trim()?.toLowerCase(),
      patientMobile: data.mobile
        ? `${data.mobileCountryCode}${data.mobile}`.trim()
        : '',
      patientName: (
        capitaliseFirstLetterOfWord(data.preferredName?.trim()) +
        ' ' +
        capitaliseFirstLetterOfWord(data.lastName?.trim())
      ).trim(),
      serviceId: matchedService.type === 'Service' ? matchedService.id : null,
      classServiceId:
        matchedService.type === 'Service' ? null : matchedService.id,
      // startTime: selectedAppointmentTime.startTime,
      startTime: zonedTimeToUtc(
        selectedAppointmentTime.startTime,
        selectedLocationTimeZone
      ),
      tenantId: tenantId,
      contactId: urlQueryParams?.contactid || null, // need to use small caps contactid due to FE sanitisation
      urlQueryParams: JSON.stringify(urlQueryParams),
      customFields: JSON.stringify({ enquiry: enquiryResponses }),
      promoCodeApplied: validPromoCode
    };
    // console.log(finalData);

    // call the API when ready
    const graphQLmutation = {
      query: `mutation CreateOnlineBookingTransaction($input: CreateOnlineBookingTransactionInput!) {\n  createOnlineBookingTransaction(input: $input) {\n    id\n    onlineBookingPageId\n    patientName\n    paymentRequired\n    stripeIntegrated\n    multiplePatients\n    stripeSetupIntentUrl\n    }\n}\n`,
      variables: { input: finalData }
    };
    const response = await axios.post(
      process.env.REACT_APP_AWS_GRAPHQL_API,
      graphQLmutation,
      {
        headers: {
          'x-api-key': process.env.REACT_APP_AWS_CONFIRMATION_API,
          accept: 'application/json, text/plain, */*',
          'accept-language': 'en-GB,en;q=0.9,en-US;q=0.8,vi;q=0.7',
          'content-type': 'application/json'
        }
      }
    );
    // console.log(response);

    if (
      response.status === 200 &&
      response.data.data.createOnlineBookingTransaction
    ) {
      if (
        response.data.data.createOnlineBookingTransaction?.paymentRequired ===
          false ||
        // response.data.data.createOnlineBookingTransaction?.stripeIntegrated ===
        //   true ||
        response.data.data.createOnlineBookingTransaction
          ?.stripeSetupIntentUrl === null
      ) {
        reset();
        // navigate('/booking/success?nextStep=Completed&event=test&location=Novena', { replace: true });
        navigate(`/booking/success?tenant=${tenantId}`, { replace: true });
      } else {
        if (
          response.data.data.createOnlineBookingTransaction
            ?.multiplePatients === true
        ) {
          setPaymentURL(
            response.data.data.createOnlineBookingTransaction
              ?.stripeSetupIntentUrl
          );
          // console.log('Open Dialog Prompt');
          setOpenPaymentPrompt(true);
        } else {
          // navigate straight to Stripe - for single patient with no previous integrations
          // console.log(
          //   response.data.data.createOnlineBookingTransaction
          //     ?.stripeSetupIntentUrl
          // );
          window.location.href =
            response.data.data.createOnlineBookingTransaction?.stripeSetupIntentUrl;
        }
      }
    } else {
      // console.log('Error');
      console.log(response.data.errors);
      navigate('/booking/error', { replace: true });
    }
  };

  const resetSelection = () => {
    Mixpanel.track('Online Booking - Reset Selection');
    if (category !== 'general') {
      setCategory('');
    }

    setTogglePracLoc('');
    setSelectedServiceName('');
    setSelectedServiceDescription('');
    setSelectedLocation('');
    setSelectedPractitioner('');
    setSelectedAppointmentTime(null);
    setDateSelectionCount(0);

    executeScrollToOriginPick();
  };

  const filterServices = useCallback(() => {
    if (categoryOptions?.length === 0 && servicesList.length > 0) {
      const filteredServiceName: [any] = servicesList;

      setCategoryOptions(
        uniqBy(filteredServiceName, 'onlineBookingCategory').map((serv) => ({
          label: serv.onlineBookingCategory,
          value: serv.onlineBookingCategory
        }))
      );
    }

    if (selectedServiceName !== '') {
      const filteredServiceName: [any] = servicesList.filter(
        (service) =>
          service.onlineBookingCategory === category &&
          service.onlineBookingName === selectedServiceName
      );

      setLocationOptions(
        uniqBy(filteredServiceName, 'locationName').map((loc) => ({
          label: loc.locationName,
          value: loc.locationName
        }))
      );

      setPractitionerOptions(
        uniqBy(filteredServiceName, 'providerName').map((prac) => ({
          label: prac.providerName,
          value: prac.providerName,
          jobTitle: prac.practitionerJobTitle
        }))
      );
      setAvailableServices(filteredServiceName);
    }
  }, [isMountedRef, selectedServiceName]);

  const getFreeSlots = useCallback(
    async (serviceId, classServiceId, startDate, endDate) => {
      const graphQLquery = {
        // operationName: 'getOnlineBookingPage',
        query: `query ListFreeAppointmentBookingSlots($startDate: AWSDate!, $endDate: AWSDate!, $serviceIds: [ID!]!, $classServiceIds: [ID!]!, $tenantId: String, $incrementInterval: Int) {\n  listFreeAppointmentBookingSlots(startDate: $startDate, endDate: $endDate, serviceIds: $serviceIds, classServiceIds: $classServiceIds, tenantId: $tenantId, , incrementInterval: $incrementInterval) {\n    startTime\n    endTime\n  }\n}\n`,
        variables: {
          serviceIds: serviceId ? [serviceId] : [],
          classServiceIds: classServiceId ? [classServiceId] : [],
          startDate,
          endDate,
          tenantId: tenantId,
          incrementInterval:
            onlineBookingMappings?.getOnlineBookingWindowPeriod(tenantId)?.[
              'appointmentIntervals'
            ] || 60
        }
      };
      axios
        .post(process.env.REACT_APP_AWS_GRAPHQL_API, graphQLquery, {
          headers: {
            // Need to change API key for different stack. this is WSD currently.
            'x-api-key': process.env.REACT_APP_AWS_CONFIRMATION_API,
            accept: 'application/json, text/plain, */*',
            'accept-language': 'en-GB,en;q=0.9,en-US;q=0.8,vi;q=0.7',
            'content-type': 'application/json'
          }
        })
        .then((res) => {
          // console.log(res);
          if (
            res.status === 200 &&
            res.data.data.listFreeAppointmentBookingSlots
          ) {
            if (res.data.data.listFreeAppointmentBookingSlots?.length <= 2) {
              setDateSelectionCount(4);
            }
            // console.log(res);
            setFreeSlots(
              // res.data.data.listFreeAppointmentBookingSlots.map((timeslot) => ({
              //   startTime: parseISO(timeslot.startTime),
              //   endTime: parseISO(timeslot.endTime)
              // }))
              res.data.data.listFreeAppointmentBookingSlots.map((timeslot) => {
                // console.log(timeslot.startTime);

                const convertedSlot = {
                  startTime: utcToZonedTime(
                    timeslot.startTime,
                    selectedLocationTimeZone
                  ),
                  endTime: utcToZonedTime(
                    timeslot.endTime,
                    selectedLocationTimeZone
                  )
                };
                // console.log(convertedSlot);

                return convertedSlot;
              })
            );
            setIsFetchingTimeSlots(false);
          } else {
            console.log('Error');
            setIsFetchingTimeSlots(false);
          }
        })
        .catch((err) => {
          console.log(err);
          setIsFetchingTimeSlots(false);
          // setError('Looks like something went wrong.');
        });
    },
    [isMountedRef]
  );

  // console.log(servicesList);
  useEffect(() => {
    // this effect helps us to preset the fields when given query params
    // console.log(urlQueryParams);
    // http://localhost:3000/booking/8a868252-ed96-4575-9f22-664624a9a41a?apptType=firstvisit&code=d27a2a40-bdc5-477e-b299-2a1d2fc0d698&displayby=practitioner&prac=0f3a9151-3810-4357-9d89-57478ce64186
    if (
      urlQueryParams?.appttype &&
      servicesList?.filter(
        (serv) =>
          serv?.onlineBookingCategory.replace(' ', '').toLowerCase() ===
          urlQueryParams?.appttype.toLowerCase()
      ).length > 0
    ) {
      const foundCategory = servicesList?.find(
        (serv) =>
          serv?.onlineBookingCategory.replace(' ', '').toLowerCase() ===
          urlQueryParams?.appttype.toLowerCase()
      )?.onlineBookingCategory;
      setCategory(foundCategory);

      if (urlQueryParams?.code) {
        const matchedService = servicesList?.find(
          (service) => service.id === urlQueryParams?.code
        );
        const serviceCategory = matchedService?.onlineBookingCategory;
        const serviceName = matchedService?.onlineBookingName;
        const serviceDescription = matchedService?.onlineBookingDescription;

        // const serviceCategory = servicesList.find(
        //   (service) => service.id === urlQueryParams?.code
        // )?.onlineBookingCategory;
        // const serviceName = servicesList.find(
        //   (service) => service.id === urlQueryParams?.code
        // )?.onlineBookingName;
        setCategory(serviceCategory);
        setSelectedServiceName(serviceName);
        setSelectedServiceDescription(serviceDescription);

        if (
          urlQueryParams?.displayby &&
          (urlQueryParams?.displayby === 'practitioner' ||
            urlQueryParams?.displayby === 'location')
        ) {
          setTogglePracLoc(urlQueryParams?.displayby);

          if (
            urlQueryParams?.displayby === 'practitioner' &&
            urlQueryParams?.prac &&
            servicesList.filter(
              (service) =>
                service.onlineBookingCategory === serviceCategory &&
                service.onlineBookingName === serviceName &&
                service.practitionerId === urlQueryParams?.prac
            ).length > 0
          ) {
            setSelectedPractitioner(
              servicesList.find(
                (service) =>
                  service.onlineBookingCategory === serviceCategory &&
                  service.onlineBookingName === serviceName &&
                  service.practitionerId === urlQueryParams?.prac
              )?.providerName
            );
            // setPresetMode('preset');
          } else if (
            urlQueryParams?.displayby === 'location' &&
            urlQueryParams?.loc &&
            servicesList.filter(
              (service) =>
                service.onlineBookingCategory === serviceCategory &&
                service.onlineBookingName === serviceName &&
                service.locationId === urlQueryParams?.loc
            ).length > 0
          ) {
            setSelectedLocation(
              servicesList.find(
                (service) =>
                  service.onlineBookingCategory === serviceCategory &&
                  service.onlineBookingName === serviceName &&
                  service.locationId === urlQueryParams?.loc
              )?.locationName
            );
          }
        }
      }
    }
  }, []);

  useEffect(() => {
    filterServices();
  }, [isMountedRef, selectedServiceName]);

  // If there is only one category, always pick that one
  useEffect(() => {
    if (category === '' && categoryOptions.length === 1) {
      setCategory(categoryOptions[0].value);
    }
  }, [isMountedRef, categoryOptions]);

  // If category has been picked and there is only one service name, always pick that one
  useEffect(() => {
    if (
      category !== '' &&
      selectedServiceName === '' &&
      uniqBy(
        servicesList.filter(
          (service) => service.onlineBookingCategory === category
        ),
        'onlineBookingName'
      ).length === 1
    ) {
      const service: any = [
        ...uniqBy(
          servicesList.filter(
            (service) => service.onlineBookingCategory === category
          ),
          'onlineBookingName'
        )
      ].at(0);

      setSelectedServiceName(service.onlineBookingName);
      setSelectedServiceDescription(service.onlineBookingDescription);
    }
  }, [isMountedRef, category]);

  useEffect(() => {
    if (
      selectedAppointmentTime === null &&
      selectedLocation !== '' &&
      selectedPractitioner !== '' &&
      selectedServiceName !== ''
    ) {
      const matchedService = servicesList.find(
        (service) =>
          service.onlineBookingCategory === category &&
          service.onlineBookingName === selectedServiceName &&
          service.providerName === selectedPractitioner &&
          service.locationName === selectedLocation
      );

      // console.log(matchedService);

      const matchedServiceId = servicesList.find(
        (service) =>
          service.onlineBookingCategory === category &&
          service.onlineBookingName === selectedServiceName &&
          service.providerName === selectedPractitioner &&
          service.locationName === selectedLocation
      )?.id;

      // console.log(matchedServiceId);

      setSelectedServiceId(matchedServiceId);
      setIsFetchingTimeSlots(true);

      getFreeSlots(
        matchedService?.type === 'Service' ? matchedServiceId : null,
        matchedService?.type === 'Class Service' ? matchedServiceId : null,
        format(
          addDays(
            new Date(),
            onlineBookingMappings?.getOnlineBookingWindowPeriod(tenantId)[
              'addDaysForStartDate'
            ]
          ),
          'yyyy-MM-dd'
        ),
        format(
          addDays(
            new Date(),
            onlineBookingMappings?.getOnlineBookingWindowPeriod(tenantId)[
              'addDaysForStartDate'
            ] +
              (allowedDaysInFuture ||
                onlineBookingMappings?.getOnlineBookingWindowPeriod(tenantId)[
                  'addDaysForEndDate'
                ])
          ),
          'yyyy-MM-dd'
        )
      );
      executeScrollToDatePicker();
    } else if (
      selectedServiceName !== '' &&
      togglePracLoc === '' &&
      selectedLocation === '' &&
      selectedPractitioner === '' &&
      selectedAppointmentTime === null
    ) {
      executeScrollToFirstPick();
    } else if (
      selectedServiceName !== '' &&
      togglePracLoc !== '' &&
      selectedLocation === '' &&
      selectedPractitioner === '' &&
      selectedAppointmentTime === null
    ) {
      executeScrollToSecondPick();
    } else if (
      selectedServiceName !== '' &&
      togglePracLoc !== '' &&
      selectedAppointmentTime === null &&
      (selectedLocation === '' || selectedPractitioner === '')
    ) {
      executeScrollToThirdPick();
    } else if (
      selectedServiceName !== '' &&
      togglePracLoc !== '' &&
      selectedLocation !== '' &&
      selectedPractitioner !== '' &&
      selectedAppointmentTime !== null
    ) {
      executeScrollToSummary();
    }
  }, [
    isMountedRef,
    selectedServiceName,
    selectedPractitioner,
    selectedLocation,
    selectedAppointmentTime,
    getFreeSlots,
    togglePracLoc
  ]);

  const uniqueDates = freeSlots.filter(
    (date, i, self) =>
      self.findIndex((d) =>
        isEqual(startOfDay(d.startTime), startOfDay(date.startTime))
      ) === i
  );

  const shouldDisableDate = (date) => {
    // return date.getDay() === 0 || date.getDay() === 6;
    return !uniqueDates.find((uniqueDate) =>
      isEqual(startOfDay(uniqueDate.startTime), startOfDay(date))
    );
  };

  if (!servicesList || servicesList.length === 0) {
    return null;
  }

  return (
    <>
      <Grid container spacing={0} justifyContent="center">
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2, mx: mobile ? 0 : 2 }}>
              <Grid container spacing={0} rowGap={1} ref={originPickRef}>
                <Grid item xs={12}>
                  {parse(contentHTML)}
                </Grid>

                {category === '' && (
                  <>
                    <Grid item xs={12}>
                      <Box
                        display="flex"
                        justifyContent="center"
                        width="100%"
                        // sx={{ mb: 1 }}
                      >
                        <Alert
                          variant="outlined"
                          severity="warning"
                          icon={false}
                          // icon={<InfoOutlinedIcon fontSize="inherit" />}
                        >
                          <AlertTitle>Select a category to begin</AlertTitle>
                        </Alert>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Stack
                        rowGap={2}
                        columnGap={2}
                        direction="row"
                        flexWrap="wrap"
                        justifyContent="center"
                      >
                        {categoryOptions
                          .sort(sortOnlineBookingCategoryAsc)
                          .map((visit, index) => (
                            <MenuItem
                              key={visit.value}
                              style={{ whiteSpace: 'normal' }}
                              sx={{
                                p: 2,
                                ml: 1,
                                mr: 1,
                                borderRadius: 1,
                                border: '1px solid #CCCCCC',
                                minWidth: 200,
                                maxWidth: 200
                              }}
                              divider
                              onClick={() => {
                                setCategory(visit.value);
                                setTogglePracLoc('');
                                setSelectedLocation('');
                                setSelectedPractitioner('');
                                setSelectedAppointmentTime(null);
                                setDateSelectionCount(0);
                              }}
                            >
                              <ListItemText
                                primary={
                                  <Typography gutterBottom variant="h4">
                                    {visit.label}
                                  </Typography>
                                }
                              />
                              <ChevronRightTwoToneIcon
                                sx={{
                                  color: `${theme.colors.alpha.black[30]}`,
                                  opacity: 0.8
                                }}
                              />
                            </MenuItem>
                          ))}
                      </Stack>
                    </Grid>
                  </>
                )}

                {category !== '' && selectedAppointmentTime === null ? (
                  <Grid item xs={12}>
                    <Grid container spacing={0}>
                      <Grid item xs={12} md={4}>
                        <Grid container spacing={0} rowGap={1} sx={{ mt: 1 }}>
                          {category !== '' ? (
                            <Grid item xs={12}>
                              <Box
                                display="flex"
                                justifyContent={{
                                  xs: 'center',
                                  md: 'flex-start'
                                }}
                                // sx={{ mb: 1 }}
                              >
                                <Alert
                                  variant="outlined"
                                  severity="warning"
                                  sx={{ maxWidth: '75%' }}
                                  icon={false}
                                  // icon={<InfoOutlinedIcon fontSize="inherit" />}
                                >
                                  {/* <AlertTitle>
                                    Step 1. Select a service
                                  </AlertTitle> */}
                                  <Typography>
                                    Step 1. Select a service
                                  </Typography>
                                </Alert>
                              </Box>
                            </Grid>
                          ) : null}

                          <Grid item xs={12}>
                            <Box
                              display="flex"
                              justifyContent={{
                                xs: 'center',
                                md: 'space-between'
                              }}
                            >
                              <Box
                                display="flex"
                                justifyContent={{
                                  xs: 'center',
                                  md: 'flex-start'
                                }}
                              >
                                <Stack
                                  rowGap={2}
                                  columnGap={2}
                                  direction="column"
                                  flexWrap="wrap"
                                >
                                  {uniqBy(
                                    servicesList.filter(
                                      (service) =>
                                        service.onlineBookingCategory ===
                                        category
                                    ),
                                    'onlineBookingName'
                                  )
                                    .sort(sortServiceNameAsc)
                                    .map((service: any) => (
                                      <MenuItem
                                        style={{ whiteSpace: 'normal' }}
                                        sx={{
                                          p: 2,
                                          borderRadius: 1,
                                          border: '1px solid #CCCCCC',
                                          minWidth: 100,
                                          maxWidth: 250
                                        }}
                                        divider
                                        onClick={() => {
                                          setSelectedServiceName(
                                            service.onlineBookingName
                                          );
                                          setSelectedServiceDescription(
                                            service.onlineBookingDescription
                                          );
                                          setTogglePracLoc('');
                                          setSelectedLocation('');
                                          setSelectedPractitioner('');
                                          setSelectedAppointmentTime(null);
                                          setDateSelectionCount(0);
                                          // executeScrollToFirstPick();
                                        }}
                                        key={service.id}
                                        selected={
                                          service.onlineBookingName ===
                                          selectedServiceName
                                        }
                                      >
                                        <ListItemText
                                          primary={
                                            <Typography
                                              // gutterBottom
                                              variant="h4"
                                            >
                                              {service.onlineBookingName}
                                            </Typography>
                                          }
                                          secondary={
                                            service.onlineBookingDescription &&
                                            service.onlineBookingDescription
                                              ?.length > 30 ? (
                                              <Tooltip
                                                placement="bottom-start"
                                                title={
                                                  service.onlineBookingDescription
                                                }
                                              >
                                                <Typography
                                                  gutterBottom
                                                  variant="body2"
                                                >
                                                  {service.onlineBookingDescription?.slice(
                                                    0,
                                                    29
                                                  )}
                                                  ...
                                                </Typography>
                                              </Tooltip>
                                            ) : (
                                              <Typography
                                                gutterBottom
                                                variant="body2"
                                              >
                                                {service.onlineBookingDescription ||
                                                  ''}
                                              </Typography>
                                            )
                                          }
                                        />
                                        <ChevronRightTwoToneIcon
                                          sx={{
                                            color: `${theme.colors.alpha.black[30]}`,
                                            opacity: 0.8
                                          }}
                                        />
                                      </MenuItem>
                                    ))}
                                </Stack>
                              </Box>

                              <Divider
                                orientation="vertical"
                                sx={{
                                  ml: 2,
                                  display: { xs: 'none', md: 'block' }
                                }}
                              />
                            </Box>
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item xs={12} md={8}>
                        <Grid container spacing={0} rowGap={1} sx={{ mt: 1 }}>
                          {selectedServiceName === '' ? (
                            <Grid item xs={12}>
                              <Box display="flex" justifyContent="end">
                                <Button
                                  startIcon={<RestartAltTwoToneIcon />}
                                  size="small"
                                  onClick={resetSelection}
                                  variant="outlined"
                                >
                                  Reset
                                </Button>
                              </Box>
                            </Grid>
                          ) : null}

                          {/* Select by location or by provider */}
                          {selectedServiceName !== '' ? (
                            <>
                              {/* Alert to select a search option */}
                              <Grid item xs={12}>
                                <Box
                                  display="flex"
                                  justifyContent="center"
                                  // width="100%"
                                  sx={{ mt: { xs: 2, md: 0 } }}
                                  ref={firstPickRef}
                                >
                                  <Alert
                                    variant="outlined"
                                    severity="warning"
                                    sx={{ maxWidth: '75%' }}
                                    icon={false}
                                    // icon={
                                    //   <InfoOutlinedIcon fontSize="inherit" />
                                    // }
                                  >
                                    {/* <AlertTitle>
                                      {`Step 2. Select a search option`}
                                    </AlertTitle> */}
                                    <Typography>
                                      Step 2. Select a search option
                                    </Typography>
                                  </Alert>
                                </Box>
                              </Grid>

                              {/* Search by options - Location or Practitioner */}
                              <Grid item xs={12}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                  }}
                                >
                                  <Stack
                                    rowGap={2}
                                    columnGap={2}
                                    direction={{ xs: 'column', md: 'row' }}
                                    flexWrap="wrap"
                                    justifyContent="center"
                                  >
                                    <MenuItem
                                      style={{ whiteSpace: 'normal' }}
                                      sx={{
                                        p: 1,
                                        mr: 1,
                                        ml: 1,
                                        borderRadius: 1,
                                        border: '1px solid #CCCCCC',
                                        minWidth: 170,
                                        maxWidth: 170,
                                        textAlign: 'center'
                                        // backgroundColor:
                                        //   theme.palette.primary.main,
                                        // color: 'white'
                                      }}
                                      divider
                                      onClick={() => {
                                        setTogglePracLoc('location');
                                        setSelectedLocation('');
                                        setSelectedPractitioner('');
                                        setSelectedAppointmentTime(null);
                                        setDateSelectionCount(0);
                                        Mixpanel.track(
                                          'Online Booking - By Location'
                                        );
                                        // executeScrollToSecondPick();
                                      }}
                                      key={'location'}
                                      selected={togglePracLoc === 'location'}
                                    >
                                      <ListItemText
                                        primary={
                                          <Typography gutterBottom variant="h4">
                                            {'By Location'}
                                          </Typography>
                                        }
                                      />
                                    </MenuItem>

                                    <MenuItem
                                      style={{ whiteSpace: 'normal' }}
                                      sx={{
                                        p: 1,
                                        mr: 1,
                                        ml: 1,
                                        borderRadius: 1,
                                        border: '1px solid #CCCCCC',
                                        minWidth: 170,
                                        maxWidth: 170,
                                        textAlign: 'center'
                                        // backgroundColor:
                                        //   theme.palette.primary.main,
                                        // color: 'white'
                                      }}
                                      divider
                                      onClick={() => {
                                        setTogglePracLoc('practitioner');
                                        setSelectedLocation('');
                                        setSelectedPractitioner('');
                                        setSelectedAppointmentTime(null);
                                        setDateSelectionCount(0);
                                        Mixpanel.track(
                                          'Online Booking - By Provider'
                                        );
                                        // executeScrollToSecondPick();
                                      }}
                                      key={'practitioner'}
                                      selected={
                                        togglePracLoc === 'practitioner'
                                      }
                                    >
                                      <ListItemText
                                        primary={
                                          <Typography gutterBottom variant="h4">
                                            {`By ${getMappedWordToUse(
                                              'Practitioner'
                                            )}`}
                                          </Typography>
                                        }
                                      />
                                    </MenuItem>
                                  </Stack>
                                </Box>
                              </Grid>
                            </>
                          ) : null}

                          {/* First Pick Ref - #1 After selection of Location -> select Provider, #2 After selection of Provider -> select Location */}
                          {selectedServiceName !== '' &&
                          togglePracLoc !== '' ? (
                            <>
                              {/* Alert to select a location or a practitioner depending on flow */}
                              <Grid item xs={12}>
                                <Box
                                  display="flex"
                                  justifyContent="center"
                                  // width="100%"
                                  sx={{ mt: 2 }}
                                  ref={secondPickRef}
                                >
                                  {/* Flow when initial selection is Location. Select Location first, then select Practitioner */}
                                  {togglePracLoc === 'location' ? (
                                    <Alert
                                      variant="outlined"
                                      severity="warning"
                                      sx={{ maxWidth: '75%' }}
                                      icon={false}
                                      // icon={
                                      //   <InfoOutlinedIcon fontSize="inherit" />
                                      // }
                                    >
                                      {/* <AlertTitle>
                                        Step 3. Select a preferred location
                                      </AlertTitle> */}
                                      <Typography>
                                        Step 3. Select a preferred location
                                      </Typography>
                                    </Alert>
                                  ) : null}

                                  {togglePracLoc === 'practitioner' ? (
                                    <Alert
                                      variant="outlined"
                                      severity="warning"
                                      sx={{ maxWidth: '75%' }}
                                      icon={false}
                                      // icon={
                                      //   <InfoOutlinedIcon fontSize="inherit" />
                                      // }
                                    >
                                      {/* <AlertTitle>
                                        {`Step 3. Select a preferred ${getMappedWordToUse(
                                          'practitioner'
                                        )}`}
                                      </AlertTitle> */}
                                      <Typography>
                                        {`Step 3. Select a preferred ${getMappedWordToUse(
                                          'practitioner'
                                        )}`}
                                      </Typography>
                                    </Alert>
                                  ) : null}
                                </Box>

                                {/* Flow when initial selection is Practitioner. Select Practitioner first, then select Location */}
                              </Grid>

                              {/* Select a location or a practitioner depending on flow */}
                              <Grid item xs={12}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                  }}
                                  // ref={firstPickRef} // error
                                >
                                  <Stack
                                    rowGap={2}
                                    columnGap={2}
                                    direction="row"
                                    justifyContent="center"
                                    flexWrap="wrap"
                                  >
                                    {togglePracLoc === 'location' &&
                                      // selectedLocation === '' &&
                                      locationOptions.map((loc, index) => (
                                        <MenuItem
                                          style={{ whiteSpace: 'normal' }}
                                          sx={{
                                            p: 1,
                                            mr: 1,
                                            ml: 1,
                                            borderRadius: 1,
                                            border: '1px solid #CCCCCC',
                                            minWidth: 100,
                                            maxWidth: 150,
                                            textAlign: 'center'
                                            // backgroundColor:
                                            //   theme.palette.primary.main,
                                            // color: 'white'
                                          }}
                                          divider
                                          onClick={() => {
                                            setSelectedLocation(loc.value);
                                            setSelectedPractitioner('');
                                            setSelectedAppointmentTime(null);
                                            setDateSelectionCount(0);
                                            // executeScrollToThirdPick();
                                          }}
                                          key={loc.value}
                                          selected={
                                            loc.value === selectedLocation
                                          }
                                        >
                                          <ListItemText
                                            primary={
                                              <Typography
                                                gutterBottom
                                                variant="h4"
                                              >
                                                {loc.label}
                                              </Typography>
                                            }
                                          />
                                        </MenuItem>
                                      ))}

                                    {togglePracLoc === 'practitioner' &&
                                      // selectedPractitioner === '' &&
                                      practitionerOptions
                                        .sort(sortPractLabelAsc)
                                        .map((prac, index) => (
                                          <MenuItem
                                            style={{ whiteSpace: 'normal' }}
                                            sx={{
                                              p: 1,
                                              mr: 1,
                                              ml: 1,
                                              borderRadius: 1,
                                              border: '1px solid #CCCCCC',
                                              minWidth: 110,
                                              maxWidth: 110,
                                              // minHeight: 120,
                                              textAlign: 'center',
                                              justifyContent: 'center'
                                            }}
                                            divider
                                            onClick={() => {
                                              setSelectedPractitioner(
                                                prac.value
                                              );
                                              setSelectedLocation('');
                                              setSelectedAppointmentTime(null);
                                              setDateSelectionCount(0);
                                              // executeScrollToThirdPick();
                                            }}
                                            key={prac.value}
                                            selected={
                                              prac.value ===
                                              selectedPractitioner
                                            }
                                          >
                                            <Stack
                                              rowGap={2}
                                              columnGap={2}
                                              justifyContent="center"
                                              alignItems="center"
                                              direction="column"
                                            >
                                              <Avatar
                                                // variant="rounded"
                                                alt={`Provider`}
                                                // src={`/static/images/profilePictures/${tenantId.toLowerCase()}/${
                                                //   servicesList.find(
                                                //     (service) =>
                                                //       service.onlineBookingCategory ===
                                                //         category &&
                                                //       service.onlineBookingName ===
                                                //         selectedServiceName &&
                                                //       service.providerName ===
                                                //         prac.value
                                                //   )?.practitionerId
                                                // }.jpg`}
                                                src={`${
                                                  servicesList.find(
                                                    (service) =>
                                                      service.onlineBookingCategory ===
                                                        category &&
                                                      service.onlineBookingName ===
                                                        selectedServiceName &&
                                                      service.providerName ===
                                                        prac.value
                                                  )?.profilePicture
                                                }`}
                                                sx={{ width: 100, height: 100 }}
                                              />

                                              <ListItemText
                                                primary={
                                                  <Typography variant="h4">
                                                    {`${prac.label}`}
                                                  </Typography>
                                                }
                                                secondary={
                                                  <Typography
                                                    gutterBottom
                                                    variant="subtitle2"
                                                    fontSize={13}
                                                    sx={{ mx: 0.5 }}
                                                  >
                                                    {`${prac.jobTitle}`}
                                                  </Typography>
                                                }
                                              />
                                            </Stack>
                                          </MenuItem>
                                        ))}
                                  </Stack>
                                </Box>
                              </Grid>

                              {/* Practitioner description if first option is Practitioner */}
                              {togglePracLoc === 'practitioner' &&
                              selectedPractitioner !== '' ? (
                                <Grid item xs={12}>
                                  {servicesList.find(
                                    (service) =>
                                      service.providerName ===
                                      selectedPractitioner
                                  )?.providerDescription && (
                                    <Paper
                                      elevation={0}
                                      // variant="outlined"
                                      sx={{
                                        p: 2,
                                        // mb: 2,
                                        width: 'auto',
                                        flexWrap: 'wrap',
                                        display: 'flex',
                                        background: '#F5F5F5',
                                        textAlign: 'center'
                                      }}
                                    >
                                      {
                                        servicesList.find(
                                          (service) =>
                                            service.providerName ===
                                            selectedPractitioner
                                        )?.providerDescription
                                      }
                                    </Paper>
                                  )}
                                </Grid>
                              ) : null}

                              {/* Alert to select a location NEXT or a practitioner NEXT depending on earlier option chosen */}
                              <Grid item xs={12}>
                                <Box
                                  display="flex"
                                  justifyContent="center"
                                  // width="100%"
                                  sx={{ mt: 2 }}
                                  ref={thirdPickRef}
                                >
                                  {togglePracLoc === 'location' &&
                                    selectedLocation !== '' && (
                                      <Alert
                                        variant="outlined"
                                        severity="warning"
                                        sx={{ maxWidth: '75%' }}
                                        icon={false}
                                        // icon={
                                        //   <InfoOutlinedIcon fontSize="inherit" />
                                        // }
                                      >
                                        {/* <AlertTitle>
                                          {`Step 4. Select a preferred ${getMappedWordToUse(
                                            'practitioner'
                                          )}`}
                                        </AlertTitle> */}
                                        <Typography>
                                          {`Step 4. Select a preferred ${getMappedWordToUse(
                                            'practitioner'
                                          )}`}
                                        </Typography>
                                      </Alert>
                                    )}

                                  {selectedServiceName !== '' &&
                                    togglePracLoc === 'practitioner' &&
                                    selectedPractitioner !== '' && (
                                      <Alert
                                        variant="outlined"
                                        severity="warning"
                                        sx={{ maxWidth: '75%' }}
                                        icon={false}
                                        // icon={
                                        //   <InfoOutlinedIcon fontSize="inherit" />
                                        // }
                                      >
                                        {/* <AlertTitle>
                                          Step 4. Select a preferred location
                                        </AlertTitle> */}
                                        <Typography>
                                          Step 4. Select a preferred location
                                        </Typography>
                                      </Alert>
                                    )}
                                </Box>
                              </Grid>

                              <Grid item xs={12}>
                                {selectedPractitioner !== '' ||
                                selectedLocation !== '' ? (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center'
                                    }}
                                  >
                                    <Stack
                                      rowGap={2}
                                      columnGap={2}
                                      direction="row"
                                      flexWrap="wrap"
                                      justifyContent="center"
                                    >
                                      {togglePracLoc === 'location' &&
                                        selectedLocation !== '' &&
                                        availableServices
                                          .filter(
                                            (service) =>
                                              service.locationName ===
                                              selectedLocation
                                          )
                                          .sort(sortPractAsc)
                                          .map((service, index) => (
                                            <MenuItem
                                              style={{ whiteSpace: 'normal' }}
                                              sx={{
                                                p: 1,
                                                ml: 1,
                                                mr: 1,
                                                // ml: index === 0 ? 2 : 0,
                                                borderRadius: 1,
                                                border: '1px solid #CCCCCC',
                                                minWidth: 110,
                                                maxWidth: 110,
                                                textAlign: 'center',
                                                justifyContent: 'center'
                                                // backgroundColor:
                                                //   theme.palette.primary.main,
                                                // color: 'white'
                                              }}
                                              divider
                                              onClick={() => {
                                                setSelectedPractitioner(
                                                  service.providerName
                                                );
                                                setSelectedAppointmentTime(
                                                  null
                                                );
                                                setDateSelectionCount(0);
                                              }}
                                              key={service.id}
                                              selected={
                                                service.providerName ===
                                                selectedPractitioner
                                              }
                                            >
                                              <Stack
                                                rowGap={2}
                                                columnGap={2}
                                                direction="column"
                                                justifyContent="center"
                                                alignItems="center"
                                                flexWrap="wrap"
                                              >
                                                <Avatar
                                                  // variant="rounded"
                                                  alt={`Provider`}
                                                  // src={`/static/images/profilePictures/${tenantId.toLowerCase()}/${
                                                  //   service.practitionerId
                                                  // }.jpg`}
                                                  src={`${service.profilePicture}`}
                                                  sx={{
                                                    width: 100,
                                                    height: 100
                                                  }}
                                                />

                                                <ListItemText
                                                  primary={
                                                    <Typography
                                                      gutterBottom
                                                      variant="h4"
                                                    >
                                                      {service.providerName}
                                                    </Typography>
                                                  }
                                                  secondary={
                                                    <Typography
                                                      gutterBottom
                                                      variant="subtitle2"
                                                      fontSize={13}
                                                      sx={{ px: 0.5 }}
                                                    >
                                                      {`${service.practitionerJobTitle}`}
                                                    </Typography>
                                                  }
                                                />
                                              </Stack>
                                            </MenuItem>
                                          ))}

                                      {selectedServiceName !== '' &&
                                        togglePracLoc === 'practitioner' &&
                                        selectedPractitioner !== '' &&
                                        availableServices
                                          .filter(
                                            (service) =>
                                              service.providerName ===
                                              selectedPractitioner
                                          )
                                          .map((service, index) => (
                                            <MenuItem
                                              style={{ whiteSpace: 'normal' }}
                                              sx={{
                                                p: 1,
                                                ml: 1,
                                                mr: 1,
                                                // ml: index === 0 ? 2 : 0,
                                                borderRadius: 1,
                                                border: '1px solid #CCCCCC',
                                                minWidth: 100,
                                                maxWidth: 150,
                                                textAlign: 'center',
                                                justifyContent: 'center'
                                                // backgroundColor:
                                                //   theme.palette.primary.main,
                                                // color: 'white'
                                              }}
                                              divider
                                              onClick={() => {
                                                setSelectedLocation(
                                                  service.locationName
                                                );
                                                setSelectedAppointmentTime(
                                                  null
                                                );
                                                setDateSelectionCount(0);
                                              }}
                                              key={service.id}
                                              selected={
                                                service.locationName ===
                                                selectedLocation
                                              }
                                            >
                                              <ListItemText
                                                primary={
                                                  <Typography
                                                    gutterBottom
                                                    variant="h4"
                                                  >
                                                    {service.locationName}
                                                  </Typography>
                                                }
                                              />
                                            </MenuItem>
                                          ))}
                                    </Stack>
                                  </Box>
                                ) : null}
                              </Grid>

                              {/* Practitioner description if first option is Location */}
                              <Grid item xs={12}>
                                {togglePracLoc === 'location' &&
                                  selectedPractitioner !== '' &&
                                  servicesList.find(
                                    (service) =>
                                      service.providerName ===
                                      selectedPractitioner
                                  )?.providerDescription && (
                                    <Paper
                                      elevation={0}
                                      // variant="outlined"
                                      sx={{
                                        p: 2,
                                        // my: 2,
                                        width: 'auto',
                                        flexWrap: 'wrap',
                                        display: 'flex',
                                        background: '#F5F5F5',
                                        textAlign: 'center'
                                      }}
                                    >
                                      {
                                        servicesList.find(
                                          (service) =>
                                            service.providerName ===
                                            selectedPractitioner
                                        )?.providerDescription
                                      }
                                    </Paper>
                                  )}
                              </Grid>
                            </>
                          ) : null}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                ) : null}

                {category !== '' &&
                selectedServiceName !== '' &&
                selectedAppointmentTime === null ? (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />

                    <Grid container spacing={0} rowGap={1} ref={datePickerRef}>
                      <Grid item xs={0} md={4} order={{ xs: 3, md: 1 }} />
                      <Grid item xs={12} md={5} order={{ xs: 3, md: 2 }}>
                        <Box
                          display="flex"
                          flexDirection="column"
                          sx={{ mb: 2 }}
                        >
                          <Typography
                            variant="h4"
                            gutterBottom
                            textAlign={{ xs: 'center', md: 'left' }}
                          >
                            Current Selection{' '}
                          </Typography>

                          <Typography
                            variant="body1"
                            textAlign={{ xs: 'center', md: 'left' }}
                          >
                            {`Location: `}
                            {selectedLocation === '' ? '' : selectedLocation}
                          </Typography>

                          <Typography
                            variant="body1"
                            textAlign={{ xs: 'center', md: 'left' }}
                          >
                            {`${getMappedWordToUse('Practitioner')}: `}
                            {selectedPractitioner === ''
                              ? ''
                              : selectedPractitioner}
                          </Typography>

                          <Typography
                            variant="body1"
                            textAlign={{ xs: 'center', md: 'left' }}
                          >
                            {`Service: `}
                            {`${
                              category === ''
                                ? ''
                                : `[${category}] ${
                                    selectedServiceName === ''
                                      ? ''
                                      : selectedServiceName
                                  }`
                            }`}
                          </Typography>

                          {selectedServiceDescription ? (
                            <Typography
                              variant="body1"
                              textAlign={{ xs: 'center', md: 'left' }}
                              // noWrap
                            >
                              <InfoTwoToneIcon fontSize="small" />

                              {selectedServiceDescription}
                            </Typography>
                          ) : null}
                        </Box>
                      </Grid>

                      {/* Hold the reset button */}
                      <Grid item xs={12} md={3} order={{ xs: 2, md: 3 }}>
                        <Box display="flex" justifyContent="flex-end">
                          <Button
                            startIcon={<RestartAltTwoToneIcon />}
                            size="small"
                            onClick={resetSelection}
                            variant="outlined"
                          >
                            Reset
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                ) : null}

                {selectedLocation !== '' &&
                  selectedPractitioner !== '' &&
                  selectedServiceName !== '' &&
                  selectedAppointmentTime === null && (
                    <Grid
                      item
                      xs={12}
                      md={4}
                      // container
                      // spacing={0}
                      // justifyContent="center"
                      // ref={datePickerRef}
                    >
                      <Box display="flex" justifyContent="center">
                        <StaticDatePicker
                          disabled={false}
                          displayStaticWrapperAs="desktop"
                          openTo="day"
                          value={dateValue}
                          onChange={(newValue) => {
                            setDateValue(newValue);
                            setDateSelectionCount((prev) => prev + 1);
                          }}
                          onMonthChange={(monthStart) => {
                            console.log(monthStart);
                            // onSearchSubmit(monthStart);
                            // if (!(getValues('practitionerId') === '')) {
                            //   checkPractitionerSchedule(new Date());
                            // }
                          }}
                          renderInput={(params) => <TextField {...params} />}
                          shouldDisableDate={shouldDisableDate}
                          minDate={addDays(
                            new Date(),
                            onlineBookingMappings?.getOnlineBookingWindowPeriod(
                              tenantId
                            )['addDaysForStartDate']
                          )}
                          maxDate={addDays(
                            new Date(),
                            onlineBookingMappings?.getOnlineBookingWindowPeriod(
                              tenantId
                            )['addDaysForStartDate'] +
                              (allowedDaysInFuture ||
                                onlineBookingMappings?.getOnlineBookingWindowPeriod(
                                  tenantId
                                )['addDaysForEndDate'])
                          )}
                          // maxDate={new Date().setMonth(new Date().getMonth() + 3)}
                        />
                      </Box>
                    </Grid>
                  )}

                {selectedLocation !== '' &&
                  selectedPractitioner !== '' &&
                  selectedServiceName !== '' &&
                  selectedAppointmentTime === null && (
                    <Grid
                      item
                      xs={12}
                      md={8}
                      // container
                      // spacing={0}
                      // justifyContent="center"
                    >
                      {isFetchingTimeSlots ? (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '300px'
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      ) : (
                        <Box
                          display="flex"
                          flexDirection="column"
                          justifyContent="center"
                        >
                          <Box
                            display="flex"
                            justifyContent="center"
                            sx={{ mb: 2 }}
                          >
                            <Typography gutterBottom variant="h4">
                              Pick a date and time:
                            </Typography>
                          </Box>

                          <Stack
                            rowGap={2}
                            columnGap={2}
                            direction="row"
                            flexWrap="wrap"
                            justifyContent="center"
                          >
                            {freeSlots
                              .filter(
                                (dayValue) =>
                                  isEqual(
                                    startOfDay(dayValue.startTime),
                                    startOfDay(dateValue)
                                  ) &&
                                  isAfter(dayValue.startTime, cutOffDateTime)
                              )
                              .map((slot, index) => (
                                <Grid item key={slot.startTime}>
                                  <MenuItem
                                    style={{ whiteSpace: 'normal' }}
                                    sx={{
                                      p: 1,
                                      ml: 1,
                                      mr: 1,
                                      // ml: index === 0 ? 2 : 0,
                                      borderRadius: 0,
                                      border: '1px solid #CCCCCC',
                                      minWidth: 100,
                                      maxWidth: 100,
                                      minHeight: 50,
                                      maxHeight: 50,
                                      textAlign: 'center'
                                    }}
                                    onClick={() => {
                                      setSelectedAppointmentTime(slot);
                                    }}
                                    key={slot.startTime}
                                    selected={
                                      slot.startTime === selectedAppointmentTime
                                    }
                                  >
                                    <ListItemText
                                      primary={
                                        <Typography
                                          gutterBottom
                                          variant="body1"
                                        >
                                          {format(slot.startTime, 'hh:mm a')}
                                        </Typography>
                                      }
                                    />
                                  </MenuItem>
                                </Grid>
                              ))}
                          </Stack>
                          {dateSelectionCount >= 4 && (
                            <Box sx={{ textAlign: 'center', mt: 3 }}>
                              <Typography
                                variant="h4"
                                gutterBottom
                                sx={{ mb: 2 }}
                              >
                                Unable to find a time slot?
                              </Typography>
                              <Button
                                variant="outlined"
                                onClick={() => setOpenRequestForm(true)}
                              >
                                Let us help you
                              </Button>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Grid>
                  )}

                {selectedLocation !== '' &&
                  selectedPractitioner !== '' &&
                  selectedServiceName !== '' &&
                  selectedAppointmentTime !== null && (
                    <>
                      <Grid item xs={12} ref={summaryRef}>
                        <Box display="flex" justifyContent="end">
                          <Button
                            startIcon={<RestartAltTwoToneIcon />}
                            size="small"
                            onClick={resetSelection}
                            variant="outlined"
                          >
                            Reset
                          </Button>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={5}>
                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <Typography variant="h4" sx={{ mb: 2 }}>
                              Booking Summary:
                            </Typography>
                            <Box
                              sx={{
                                mb: 2,
                                justifyContent: 'center',
                                display: 'flex'
                              }}
                            >
                              <Avatar
                                // variant="rounded"
                                alt={`Provider`}
                                // src={`/static/images/profilePictures/${tenantId.toLowerCase()}/${
                                //   servicesList.find(
                                //     (service) =>
                                //       service.onlineBookingCategory ===
                                //         category &&
                                //       service.onlineBookingName ===
                                //         selectedServiceName &&
                                //       service.providerName ===
                                //         selectedPractitioner
                                //   )?.practitionerId
                                // }.jpg`}

                                src={`${
                                  servicesList.find(
                                    (service) =>
                                      service.onlineBookingCategory ===
                                        category &&
                                      service.onlineBookingName ===
                                        selectedServiceName &&
                                      service.providerName ===
                                        selectedPractitioner
                                  )?.profilePicture
                                }`}
                                sx={{
                                  width: 140,
                                  height: 140
                                }}
                              />
                            </Box>

                            <Typography
                              variant="h4"
                              sx={{ textAlign: 'center', mb: 1 }}
                            >
                              <CalendarTodayTwoToneIcon
                                color="primary"
                                fontSize="large"
                              />
                            </Typography>
                            <Typography
                              variant="h4"
                              sx={{ textAlign: 'center', mb: 1 }}
                            >
                              {`${format(
                                selectedAppointmentTime.startTime,
                                // utcToZonedTime(
                                //   selectedAppointmentTime.startTime,
                                //   selectedLocationTimeZone
                                // ),
                                'EEE, dd MMM yyyy, hh:mm a'
                              )} (GMT ${
                                selectedLocationTimeZoneOffsetConv > 0
                                  ? `+${selectedLocationTimeZoneOffsetConv}`
                                  : selectedLocationTimeZoneOffsetConv
                              })`}
                            </Typography>

                            <Typography
                              variant="subtitle2"
                              sx={{ textAlign: 'center' }}
                            >
                              {`${category === '' ? '' : category}: `}
                              {`${
                                selectedServiceName === ''
                                  ? ''
                                  : selectedServiceName
                              } `}
                            </Typography>

                            <Typography
                              variant="subtitle2"
                              sx={{ textAlign: 'center', mb: 1 }}
                            >
                              {selectedPractitioner === ''
                                ? ''
                                : `with ${selectedPractitioner} `}{' '}
                              {selectedLocation === ''
                                ? ''
                                : `at ${selectedLocation} `}
                            </Typography>

                            <Typography
                              variant="subtitle2"
                              sx={{ textAlign: 'center', mb: 1 }}
                            >
                              Directions:{' '}
                              <a
                                href={`${
                                  'https://www.google.com/maps/search/?api=1&query=' +
                                  locationDetails
                                    ?.find(
                                      (loc) => loc.code === selectedLocation
                                    )
                                    ?.addressStreetAddress?.replaceAll(
                                      ',',
                                      '%2C'
                                    )
                                    .replaceAll(' ', '+') +
                                  '+' +
                                  locationDetails
                                    ?.find(
                                      (loc) => loc.code === selectedLocation
                                    )
                                    ?.addressCountry?.replaceAll(',', '%2C')
                                    .replaceAll(' ', '+') +
                                  '+' +
                                  locationDetails
                                    ?.find(
                                      (loc) => loc.code === selectedLocation
                                    )
                                    ?.addressPostalCode?.replaceAll(',', '%2C')
                                    .replaceAll(' ', '+')
                                }`}
                                target="_blank"
                                rel="noopener"
                              >
                                {'Google Map'}
                              </a>
                            </Typography>

                            <Typography
                              variant="subtitle2"
                              sx={{ mb: 2, textAlign: 'center' }}
                            >
                              <Button
                                variant="outlined"
                                onClick={() => {
                                  if (errorMessage !== '') {
                                    setErrorMessage('');
                                  }
                                  setSelectedAppointmentTime(null);
                                }}
                              >
                                Find another time
                              </Button>
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    </>
                  )}

                {selectedLocation !== '' &&
                  selectedPractitioner !== '' &&
                  selectedServiceName !== '' &&
                  selectedAppointmentTime !== null && (
                    <>
                      <Grid item xs={12} sm={7}>
                        <form
                          onSubmit={(e) => {
                            handleSubmit(onSubmit)(e).catch(() => {
                              // console.log('errorrrr');
                              setErrorMessage(
                                'This time slot is no longer available. Please find another one instead.'
                              );
                            });
                          }}
                        >
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={12}>
                              <Typography variant="h4">
                                {`${getMappedWordToUse('Patient')} Details:`}
                              </Typography>
                            </Grid>

                            <Grid item xs={12} md={7}>
                              <HookFormInputField
                                name="preferredName"
                                label={getFieldLabels('preferredName')}
                                control={control}
                                errors={errors}
                                rules={{
                                  required:
                                    getFieldMandatoryRequirements(
                                      'preferredName'
                                    )
                                }}
                                autoFocus={true}
                              />
                            </Grid>

                            {getOptionalFieldsToShow('lastName') ? (
                              <Grid item xs={12} md={5}>
                                <HookFormInputField
                                  name="lastName"
                                  label={getFieldLabels('lastName')}
                                  control={control}
                                  errors={errors}
                                  rules={{
                                    required:
                                      getFieldMandatoryRequirements('lastName')
                                  }}
                                />
                              </Grid>
                            ) : null}

                            <Grid item xs={12} md={7}>
                              <HookFormCountryField
                                name="mobileCountryCode"
                                label={getFieldLabels('mobileCountryCode')}
                                control={control}
                                errors={errors}
                                notClearable
                                result="phone"
                                rules={{
                                  required:
                                    getFieldMandatoryRequirements(
                                      'mobileCountryCode'
                                    )
                                }}
                              />
                            </Grid>

                            <Grid item xs={12} md={5}>
                              <HookFormInputField
                                name="mobile"
                                label={getFieldLabels('mobile')}
                                control={control}
                                errors={errors}
                                rules={{
                                  required:
                                    getFieldMandatoryRequirements('mobile'),
                                  pattern: {
                                    value: /^\d{7,12}$/,
                                    message:
                                      'Please enter a valid mobile number.'
                                  }
                                }}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <HookFormInputField
                                name="email"
                                label={getFieldLabels('email')}
                                control={control}
                                errors={errors}
                                rules={{
                                  required:
                                    getFieldMandatoryRequirements('email'),
                                  pattern: {
                                    value:
                                      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                                    message:
                                      'Please enter a valid email address'
                                  }
                                }}
                              />
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="subtitle2">
                                  <InfoOutlinedIcon
                                    color="primary"
                                    fontSize="small"
                                  />

                                  {`If you are submitting a booking on behalf of someone, kindly provide their details instead.`}
                                </Typography>
                              </Box>
                            </Grid>

                            {enquiry && enquiry?.length > 0
                              ? enquiry?.map((seg, i) => {
                                  // console.log(seg);
                                  if (seg?.type === 'select') {
                                    // console.log(seg?.options);
                                    return (
                                      <Grid
                                        item
                                        xs={12}
                                        md={12}
                                        key={seg.question}
                                      >
                                        <HookFormAutocompleteField
                                          name={`question${i}`}
                                          label={seg?.question}
                                          options={seg?.options}
                                          control={control}
                                          errors={errors}
                                          rules={{
                                            required: false
                                          }}
                                        />
                                      </Grid>
                                    );
                                  }

                                  if (seg?.type === 'multiSelect') {
                                    // console.log(seg?.options);
                                    return (
                                      <Grid
                                        item
                                        xs={12}
                                        md={12}
                                        key={seg.question}
                                      >
                                        <HookFormMultiAutocompleteField
                                          name={`question${i}`}
                                          label={seg?.question}
                                          options={seg?.options}
                                          control={control}
                                          errors={errors}
                                          rules={{
                                            required: false
                                          }}
                                        />
                                      </Grid>
                                    );
                                  }

                                  if (seg?.type === 'input') {
                                    return (
                                      <Grid
                                        item
                                        xs={12}
                                        md={12}
                                        key={seg.question}
                                      >
                                        <HookFormInputField
                                          name={`question${i}`}
                                          label={seg?.question}
                                          control={control}
                                          errors={errors}
                                          rules={{
                                            required: false
                                          }}
                                        />
                                      </Grid>
                                    );
                                  }

                                  if (seg?.type === 'multilineInput') {
                                    return (
                                      <Grid
                                        item
                                        xs={12}
                                        md={12}
                                        key={seg.question}
                                      >
                                        <HookFormMultilineInputField
                                          name={`question${i}`}
                                          label={seg?.question}
                                          control={control}
                                          errors={errors}
                                          rules={{
                                            required: false
                                          }}
                                          rows={2}
                                        />
                                      </Grid>
                                    );
                                  }

                                  if (seg?.type === 'textDisplay') {
                                    return (
                                      <Grid
                                        item
                                        xs={12}
                                        md={12}
                                        key={seg.question}
                                      >
                                        <Typography variant="h4">
                                          {seg?.question}
                                        </Typography>
                                      </Grid>
                                    );
                                  }
                                  if (seg?.type === 'compulsoryCheckbox') {
                                    return (
                                      <Grid
                                        item
                                        xs={12}
                                        md={12}
                                        key={seg.question}
                                      >
                                        <HookFormCheckboxField
                                          name={`question${i}`}
                                          label={seg?.question}
                                          control={control}
                                          errors={errors}
                                          rules={{
                                            required:
                                              'This is a required field.'
                                          }}
                                        />
                                      </Grid>
                                    );
                                  }
                                })
                              : null}

                            <Grid item xs={12}>
                              {errorMessage && errorMessage !== '' ? (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="body2" color={'red'}>
                                    {errorMessage}
                                  </Typography>
                                </Box>
                              ) : null}

                              {warningMessage && warningMessage !== '' ? (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="body2" color={'red'}>
                                    {warningMessage}
                                  </Typography>
                                </Box>
                              ) : null}
                            </Grid>

                            <Grid item xs={12} md={12}>
                              <PublicPromoCodeInput
                                tenantId={tenantId}
                                onlineBookingPageId={onlineBookingPageId}
                                serviceId={selectedServiceId}
                                isCheckingPromoCode={isCheckingPromoCode}
                                setIsCheckingPromoCode={setIsCheckingPromoCode}
                                setValidPromoCode={setValidPromoCode}
                              />
                            </Grid>

                            <Grid
                              item
                              xs={12}
                              md={12}
                              sx={{ mt: 3, textAlign: 'right' }}
                            >
                              <Button
                                type="submit"
                                startIcon={
                                  isSubmitting ? (
                                    <CircularProgress size="1rem" />
                                  ) : null
                                }
                                disabled={isSubmitting || isCheckingPromoCode}
                                variant="contained"
                                // color="success"
                              >
                                {'Proceed'}
                              </Button>
                            </Grid>
                          </Grid>
                        </form>
                      </Grid>
                    </>
                  )}

                <Grid item xs={12}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 3
                    }}
                  >
                    <Divider
                      sx={{
                        mt: 3
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" sx={{ my: 2 }}>
            <a href="https://gethelm.io/" target="_blank" rel="noopener">
              {'Powered by Helm'}
            </a>
          </Box>
        </Grid>
      </Grid>
      <Dialog
        // fullWidth
        maxWidth="sm"
        open={openPaymentPrompt}
        onClose={(event, reason) => {
          return;
        }}
      >
        <DialogContent
          sx={{
            p: 3
          }}
        >
          <Typography variant="h4" gutterBottom>
            {
              'Please take note that your card details will be linked with every account that shares the same email address.'
            }
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 3
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              window.location.reload();
              return null;
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              window.location.href = paymentURL;
              return null;
            }}
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>

      {selectedServiceId && (
        <BookingRequestForm
          openDialog={openRequestForm}
          setOpenDialog={setOpenRequestForm}
          locationOptions={locationDetails.map((location) => ({
            label: location.code,
            value: location.id
          }))}
          tenantId={tenantId}
          selectedLocation={selectedLocation}
          selectedPractitioner={selectedPractitioner}
          selectedService={selectedServiceName}
        />
      )}
    </>
  );
};

export default Scheduler;
